/**
 * Builds a deep-link URL into the in-platform Chat UI (`/chat`).
 *
 * The chat view (`src/ui/views/internal/chat-view`) reads the optional query
 * params on mount and clears them from the URL afterwards so a refresh /
 * shared link doesn't carry them around:
 *
 *   - `prompt`     — visible text auto-sent on mount, populating the user's
 *                    first message.
 *   - `entityId`   — id of the model entity the question is about. When set
 *                    together with `entityType`, the chat client prepends a
 *                    hidden context line to the payload sent to `/api/chat`
 *                    so the backend / model can look up the right entity,
 *                    while the user only sees the visible prompt in their
 *                    own bubble.
 *   - `entityType` — counterpart to `entityId` (e.g. `company`, `group`,
 *                    `portfolio`). Always passed alongside `entityId`.
 *
 * A stored conversation is **not** a query param: it is the path, `/chat/:conversationId`
 * (PLT-6610), built by `views/unified-shell/unified-shell-chat-routes.ts`. That is what makes a
 * conversation linkable and reloadable, which a param stripped on mount was not. This helper
 * builds the entry points into a *new* turn, so it has no business naming one.
 *
 * Call sites that still use `/chat?prompt=…` deep-linking (rather than the
 * in-page `ChatSidePanel`): the AI test workbench's "Open in /chat" row
 * action, and the company-view drilldown's "ask in chat" row action.
 * Contextual entry-points that need to seed row-level identifiers (e.g. the
 * R/O table's "Modelling details" icon — PLT-4676) bypass this helper
 * entirely and call `openChatPanel({ extra })` from `chat-panel-state.ts`
 * instead, so the chat opens in-page without a URL round-trip and without
 * exposing the row payload in the address bar.
 *
 * The legacy `/internal/chat` path now redirects to `/chat` while preserving
 * query params, so older bookmarks keep working.
 */
import { type CompanyLikeEntityType, companyLikeEntityTypes } from '@upright/common/model/input/types';

/**
 * The entity types a chat can be bound to. The three every consumer of the binding handles:
 * the context chip links each to its own page (`chat-context-card.tsx`), the welcome
 * suggestions key off them, and the model's group tools resolve a fund or portfolio from the
 * prefixed id the binding already carries (`resolve-groupfolio.ts`). Typed as the enum rather
 * than `string` so a launcher cannot bind a type nothing downstream renders (PLT-6532).
 */
export type ChatEntityType = CompanyLikeEntityType;

/**
 * What a launcher binds: the one entity every prompt it sends is about, and the label the
 * context chip shows for it on `/chat`. Shared by the launcher composer and its prompt cards so
 * the two cannot bind different things on one page.
 *
 * `entityLabel` is required here although `buildChatUrl` takes it as optional: `chat-view`
 * publishes the visible chip only when all three params are present, so a launcher that omits
 * the label ships a chat with no visible sign of what the prompt is about. The deep links that
 * want the hidden binding and no chip call `buildChatUrl` directly.
 */
export interface ChatEntityBinding {
  entityId: string;
  entityType: ChatEntityType;
  entityLabel: string;
}

/**
 * Narrows the plain `string` a published page binding carries (`CurrentPageChatBinding`
 * leaves room for values outside the enum) to a type the chat binds, or `null` for anything
 * else. Every publisher today passes a company, a group or a portfolio, so on the current
 * publishers this never returns `null`.
 */
export function toChatEntityType(raw: string | null | undefined): ChatEntityType | null {
  if (raw === null || raw === undefined) return null;
  return (companyLikeEntityTypes as readonly string[]).includes(raw) ? (raw as ChatEntityType) : null;
}

export interface ChatUrlOptions {
  /**
   * Visible text auto-sent on mount. Optional: the "Ask AI" entry points
   * (top-bar / DMA header / mobile burger) build a context-only URL with no
   * prompt — they open the composer for the user to type their own question
   * rather than auto-submitting a canned one.
   */
  prompt?: string;
  /**
   * Entry-point hint consumed by `HeaderMaybe` to pick the `/chat` logo's
   * back-destination: `'dma'` → `/dashboard`, `'public'` → `/`. Stripped
   * from the URL after mount along with the other params.
   */
  from?: 'public' | 'dma';
  /**
   * Optional model entity reference — paired; pass both or neither. A company, a group (fund)
   * or a portfolio: the chat view reads the type back as a string and every consumer of it
   * handles those three, so nothing else can be built here.
   */
  entityId?: string;
  entityType?: ChatEntityType;
  /**
   * Optional human-readable label for the *visible* chat-context chip shown
   * in full `/chat` (PLT-5051). When provided alongside `entityId` /
   * `entityType`, the chat view publishes a sticky page-chat binding so the
   * context chip renders — matching how a contextual chat opened from the
   * side panel shows its chip once expanded to full screen. The entity
   * binding itself stays hidden in the URL; the chip only appears on the
   * full-screen surface, never in the originating input.
   *
   * Deep-links that want only the hidden model binding and no visible chip
   * (the AI test workbench's "Open in /chat", the company drilldown's "ask
   * in chat") simply omit it — their behaviour is unchanged.
   */
  entityLabel?: string;
  /**
   * Tags the auto-sent first message with its source for chat-usage
   * analytics (mirrors `composerState.pendingSource`). Same allow-list the
   * chat view validates `?source=` against — anything else is dropped there,
   * and the backend schema rejects it outright.
   *
   * `campaign_link` marks a message the platform submits on the reader's
   * behalf when they arrive from an outbound campaign link. It has to be a
   * value of its own: without it such a message is logged under the default
   * and counted as typed engagement, and campaign arrivals cannot be found in
   * the log at all.
   *
   * Omit to leave the message logged as the default `user_input`.
   */
  source?: 'welcome_suggestion' | 'followup_suggestion' | 'user_input' | 'campaign_link';
}

export function buildChatUrl(options: ChatUrlOptions): string {
  const params = new URLSearchParams();
  if (options.from !== undefined) {
    params.set('from', options.from);
  }
  if (options.prompt !== undefined && options.prompt !== '') {
    params.set('prompt', options.prompt);
  }
  if (options.entityId !== undefined && options.entityType !== undefined) {
    params.set('entityId', options.entityId);
    params.set('entityType', options.entityType);
    if (options.entityLabel !== undefined && options.entityLabel !== '') {
      params.set('entityLabel', options.entityLabel);
    }
  }
  if (options.source !== undefined) {
    params.set('source', options.source);
  }
  return `/chat?${params.toString()}`;
}
